import { Link, useSearchParams } from "react-router-dom";
import { format } from "date-fns";
import { useArticle } from "../../lib/articles";
import { useCan, useMe } from "../../lib/auth";
import { BlockRenderer } from "../editor/BlockRenderer";
import { articleEditorConfig } from "../editor/articleConfig";

/**
 * Shared article-read body — used by both public and dashboard surfaces. Both
 * arrive via real navigation (no modal layer for articles), so the only thing
 * that varies per surface is the back-link and related-event paths.
 *
 * Auth/role gates are derived inside the component (canEdit) — callers don't
 * have to compute them.
 */

export type ArticleBasePath = "/news" | "/dashboard/articles";
export type EventBasePath = "/calendar" | "/dashboard/calendar";

interface ArticleViewProps {
  slug: string;
  basePath: ArticleBasePath;
  eventBasePath: EventBasePath;
}

export function ArticleView({ slug, basePath, eventBasePath }: ArticleViewProps) {
  const [params] = useSearchParams();
  const isPreviewParam = params.get("preview") === "1";
  const me = useMe();
  const articleQ = useArticle(slug);
  const a = articleQ.data?.article;

  const canEditAny = useCan("articles.edit_any");
  const canEditOwn = useCan("articles.edit_own");

  if (articleQ.isLoading) {
    return (
      <p className="font-display-italic text-[var(--color-ink-mute)]">Loading…</p>
    );
  }
  if (!a) {
    return (
      <div>
        <h2 className="font-display text-[1.85rem] leading-tight mb-4">Not found.</h2>
        <Link to={basePath} className="btn-ghost">
          <span>← All news</span>
        </Link>
      </div>
    );
  }

  const isOwn = !!me.data?.user && a.authorId === me.data.user.id;
  const canEdit = canEditAny || (canEditOwn && isOwn);
  const showPreviewBanner = isPreviewParam || a.status !== "published";

  return (
    <article className="article-view">
      {showPreviewBanner && (
        <div className="veda-banner is-preview mb-7">
          <p className="font-grotesk text-[0.7rem] tracking-[0.16em] uppercase text-[var(--color-violet)]">
            Preview · status: {a.status}
          </p>
          <Link
            to={`/dashboard/articles/${a.slug}/edit`}
            className="font-grotesk text-[0.7rem] tracking-[0.16em] uppercase text-[var(--color-ink)] hover:text-[var(--color-violet)]"
          >
            Continue editing →
          </Link>
        </div>
      )}

      <header className="mb-8 sm:mb-9">
        <p className="eyebrow mb-3">
          {a.category.replace(/-/g, " ")}
          <span className="mx-2 text-[var(--color-ink-mute)]">·</span>
          {a.publishedAt ? format(new Date(a.publishedAt), "d LLLL yyyy") : "Unpublished"}
          {a.status !== "published" && (
            <>
              <span className="mx-2 text-[var(--color-ink-mute)]">·</span>
              <span className="text-[var(--color-violet)]">{a.status}</span>
            </>
          )}
        </p>
        <h1 className="font-display text-[clamp(2rem,5.5vw,4rem)] leading-[1.05] tracking-[-0.02em] mb-4">
          {a.title}
        </h1>
        {a.subtitle && (
          <p className="font-display-italic text-[clamp(1.15rem,2.4vw,1.65rem)] text-[var(--color-ink-mute)] max-w-[50ch] text-balance">
            {a.subtitle}
          </p>
        )}
      </header>

      {a.coverImage && (
        <div className="image-frame article-cover mb-10 sm:mb-12">
          <img src={a.coverImage} alt="" />
        </div>
      )}

      <hr className="rule-violet w-12 mb-9" />

      {a.relatedEvent && (
        <div className="veda-panel mb-9">
          <p className="font-grotesk text-[0.62rem] tracking-[0.18em] uppercase text-[var(--color-violet)] mb-2">
            Related event
          </p>
          <Link
            to={`${eventBasePath}/${a.relatedEvent.slug}`}
            className="font-display text-[1.35rem] tracking-tight leading-[1.2] hover:text-[var(--color-violet)]"
          >
            {a.relatedEvent.title}
          </Link>
          <p className="font-grotesk text-[0.7rem] tracking-[0.14em] uppercase text-[var(--color-ink-mute)] mt-1.5">
            {format(new Date(a.relatedEvent.startsAt), "EEEE, d LLLL yyyy · HH:mm")}
            <span className="mx-2">·</span>
            {a.relatedEvent.locationType}
          </p>
        </div>
      )}

      <BlockRenderer
        doc={a.contentJson}
        config={articleEditorConfig}
        htmlFallback={a.contentHtml}
        className={`template-${a.templateKey}`}
      />

      <hr className="rule mt-12 sm:mt-14 mb-7" />

      <div className="flex flex-wrap items-baseline justify-between gap-4">
        <div className="font-grotesk text-[0.7rem] tracking-[0.18em] uppercase text-[var(--color-ink-mute)]">
          {a.authorName && <>By {a.authorName} · </>}
          {(a.tags ?? []).map((t) => (
            <span key={t} className="mr-3">
              #{t}
            </span>
          ))}
        </div>
        <div className="flex flex-wrap gap-2">
          <Link to={basePath} className="btn-ghost">
            <span>← All news</span>
          </Link>
          {canEdit && (
            <Link to={`/dashboard/articles/${a.slug}/edit`} className="btn-primary">
              <span>Edit article</span>
              <span className="btn-arrow-icon" aria-hidden>→</span>
            </Link>
          )}
        </div>
      </div>
    </article>
  );
}
